// Typed client for the IP Case Sheet API.
//
// Built on lib/api/client.ts (the contract client), never the legacy
// src/api.js — these are all post-contract endpoints, so they get ETag
// support, machine-readable error codes and automatic branch headers for
// free, and none of them has a legacy caller to preserve.
import { api, type Versioned, ApiError } from '@/lib/api/client';
import { getAuthToken, getActiveBranchId } from '@/lib/api/session';
import type {
  CaseSheet, Timeline, TimelineType, GapCheck, DischargeReadiness, ClinicalGist,
  IpPrintProfile, MrdChecklist, CaseSheetExportInput, CaseSheetExportResult, CaseSheetDeficiencies,
  SpecialtyRecord, SpecialtyRecordTemplateOption, SpecialtyTemplate,
  AdmissionAssessment, AssessmentInput,
  ProgressNote, ProgressNoteInput,
  NursingNote, NursingNoteInput,
  Vitals, FluidBalance, FluidDirection,
  MedicationOrder, MedicationOrderInput, MedicationAdministration, AdministrationInput, Mar,
  Investigation, InvestigationInput, Procedure, ProcedureInput,
  Consult, ConsultInput, ConsultStatus,
  CarePlanActivity, CarePlanInput,
  BedTransfer, AlertRule,
  MedicineSearchResult,
  DispenseOrderInput, DispenseOrderResult,
  AdmissionParticulars, ParticularsInput, Attender, AttenderInput, Mlc, MlcInput, PayerScheme, AdmissionPayer, PayerInput, PayerMasterOption,
  Consent, ConsentInput, NursingAssessment, NursingAssessmentInput, RiskScreen, RiskScreenInput,
  ClinicalOrder, ClinicalOrderInput, AdmissionDiagnosis, DiagnosisInput,
  DischargeSummary, DischargeSummaryInput,
  LegalHold,
} from './types';

const ip = (admissionId: number, path: string) => `/ip/admissions/${admissionId}${path}`;

// ── Case sheet aggregate ─────────────────────────────────────────

/** First paint. One round-trip for header + snapshot + deterministic alerts. */
export function fetchCaseSheet(admissionId: number): Promise<CaseSheet> {
  return api.get(ip(admissionId, '/case-sheet'));
}

export function fetchTimeline(
  admissionId: number,
  opts: { from?: string; to?: string; types?: TimelineType[]; author?: string; limit?: number; offset?: number } = {}
): Promise<Timeline> {
  return api.get(ip(admissionId, '/timeline'), {
    query: {
      from: opts.from, to: opts.to,
      types: opts.types?.length ? opts.types.join(',') : undefined,
      author: opts.author, limit: opts.limit, offset: opts.offset,
    },
  });
}

export function fetchGapCheck(admissionId: number): Promise<GapCheck> {
  return api.get(ip(admissionId, '/gap-check'));
}

export function fetchDischargeReadiness(admissionId: number): Promise<DischargeReadiness> {
  return api.get(ip(admissionId, '/discharge-readiness'));
}

/**
 * POST rather than GET because it runs a model call — it is not cacheable and
 * must not be prefetched by anything that walks links.
 */
export function generateGist(admissionId: number, provider?: string, model?: string): Promise<ClinicalGist> {
  return api.post(ip(admissionId, '/clinical-gist'), { provider, model });
}

/**
 * Freezes the read layer into a .docx and files it in Patient Records →
 * Documents, exactly like an approved AI consultation document — see
 * docs/HMS_V4_IP_CASE_SHEET_GENERATION_SPEC.md. POST because it has a
 * side effect (a new patient_documents row); not cacheable.
 */
export function generateCaseSheetDocument(admissionId: number): Promise<{ document_id: number; uhid: string }> {
  return api.post(ip(admissionId, '/case-sheet-document'));
}

// ── Print profiles, preview & export (Phase 6/7) ──────────────────
// docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md §7, §9.1, §10 Phases 6-7.

/** The four seeded copy types (code/label/watermark/whether a reason+recipient and release rights are required). Tenant-wide, not admission-scoped. */
export function fetchPrintProfiles(): Promise<IpPrintProfile[]> {
  return api.get('/ip/print-profiles');
}

export function fetchMrdChecklist(admissionId: number, profile?: string): Promise<MrdChecklist> {
  return api.get(ip(admissionId, '/mrd-checklist'), { query: { profile } });
}

/**
 * Returns the rendered HTML string (not JSON) — the exact same renderer the
 * PDF export uses, so Preview shows precisely what Generate would file. Not
 * built on `api.get`, which always parses a JSON body; this is a thin,
 * independent fetch using the same auth/branch headers client.ts attaches,
 * same precedent as src/api.ts's fetchPatientDocumentBlob.
 */
export async function fetchCaseSheetPreviewHtml(admissionId: number, profile: string): Promise<string> {
  const token = getAuthToken();
  const branchId = getActiveBranchId();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    if (branchId !== null) headers['X-Branch-Id'] = String(branchId);
  }
  const res = await fetch(`/api${ip(admissionId, '/case-sheet-preview')}?profile=${encodeURIComponent(profile)}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.error || `HTTP ${res.status}`, 'exception', res.status, err.details);
  }
  return res.text();
}

/**
 * The release-of-information register's actual writer (PDF only — see the
 * route's own header comment on why DOCX isn't a format option here yet).
 * A 409 means blocking deficiencies on a finality-gated profile with no
 * `override_reason` yet — callers read `(e as ApiError).details?.deficiencies`
 * off the caught error to show the override UI, rather than this function
 * swallowing that shape.
 */
export function generateCaseSheetExport(admissionId: number, input: CaseSheetExportInput): Promise<CaseSheetExportResult> {
  return api.post(ip(admissionId, '/case-sheet-exports'), input);
}

/** Narrows an ApiError's `details` down to the deficiencies shape the 409 above carries, or null for any other error. */
export function blockedDeficiencies(e: unknown): CaseSheetDeficiencies | null {
  if (e instanceof ApiError && e.code === 'conflict' && e.details?.deficiencies) {
    return e.details.deficiencies as CaseSheetDeficiencies;
  }
  return null;
}

// ── Specialty add-ons (Surgical / OBG / Paediatric — Phase 8) ─────

export function fetchSpecialtyRecordTemplates(): Promise<SpecialtyRecordTemplateOption[]> {
  return api.get('/ip/specialty-record-templates');
}
export function fetchSpecialtyRecords(admissionId: number): Promise<SpecialtyRecord[]> {
  return api.get(ip(admissionId, '/specialty-records'));
}
/** Null when nothing has been attached for that template yet — a normal state, not a 404. */
export function fetchSpecialtyRecord(admissionId: number, template: SpecialtyTemplate): Promise<SpecialtyRecord | null> {
  return api.get(ip(admissionId, `/specialty-records/${template}`));
}
/** Upsert the draft — creates the row the first time a clinician fills anything in for that template ("opts in", §8). Refused (422) once signed. */
export function saveSpecialtyRecord(admissionId: number, template: SpecialtyTemplate, data: Record<string, unknown>): Promise<SpecialtyRecord> {
  return api.put(ip(admissionId, `/specialty-records/${template}`), { data });
}
export function signSpecialtyRecord(id: number): Promise<SpecialtyRecord> {
  return api.post(`/ip/specialty-records/${id}/sign`, {});
}

// ── Assessment ───────────────────────────────────────────────────

/** Returns null when no assessment exists yet — a normal state, not a 404. */
export function fetchAssessment(admissionId: number): Promise<AdmissionAssessment | null> {
  return api.get(ip(admissionId, '/assessment'));
}

export function fetchAssessmentVersioned(admissionId: number): Promise<Versioned<AdmissionAssessment | null>> {
  return api.getVersioned(ip(admissionId, '/assessment'));
}

export function saveAssessment(admissionId: number, input: AssessmentInput, ifMatch?: string): Promise<AdmissionAssessment> {
  return api.put(ip(admissionId, '/assessment'), input, { ifMatch });
}

export function signAssessment(admissionId: number): Promise<AdmissionAssessment> {
  return api.post(ip(admissionId, '/assessment/sign'));
}

/**
 * Records that allergies or home medications were REVIEWED — not what was
 * found. The allergies/patient_medications tables stay authoritative; this
 * only separates "nobody asked" from "asked, nothing found".
 */
export function recordReview(admissionId: number, what: 'allergy' | 'medication'): Promise<AdmissionAssessment> {
  return api.post(ip(admissionId, '/assessment/review'), { what });
}

// ── Progress notes ───────────────────────────────────────────────

export function fetchProgressNotes(admissionId: number, includeSuperseded = false): Promise<ProgressNote[]> {
  return api.get(ip(admissionId, '/progress-notes'), {
    query: { include_superseded: includeSuperseded ? 1 : undefined },
  });
}

export function createProgressNote(admissionId: number, input: ProgressNoteInput): Promise<ProgressNote> {
  return api.post(ip(admissionId, '/progress-notes'), input);
}

/** Drafts only — the server returns 422 for a signed note, by design. */
export function updateProgressNote(noteId: number, input: ProgressNoteInput, ifMatch?: string): Promise<ProgressNote> {
  return api.patch(`/ip/progress-notes/${noteId}`, input, { ifMatch });
}

export function signProgressNote(noteId: number): Promise<ProgressNote> {
  return api.post(`/ip/progress-notes/${noteId}/sign`);
}

/**
 * Creates a NEW note referencing the original, which keeps status='signed'
 * and gains superseded_by_note_id. Nothing is overwritten.
 */
export function amendProgressNote(
  noteId: number, amendment_reason: string, changes: ProgressNoteInput
): Promise<ProgressNote> {
  return api.post(`/ip/progress-notes/${noteId}/amend`, { amendment_reason, ...changes });
}

// ── Nursing notes ────────────────────────────────────────────────

export function fetchNursingNotes(admissionId: number, shift?: string): Promise<NursingNote[]> {
  return api.get(ip(admissionId, '/nursing-notes'), { query: { shift } });
}

export function createNursingNote(admissionId: number, input: NursingNoteInput): Promise<NursingNote> {
  return api.post(ip(admissionId, '/nursing-notes'), input);
}

export function updateNursingNote(noteId: number, input: NursingNoteInput, ifMatch?: string): Promise<NursingNote> {
  return api.patch(`/ip/nursing-notes/${noteId}`, input, { ifMatch });
}

// ── Vitals ───────────────────────────────────────────────────────

/**
 * Defaults to a 24-hour window server-side. Widening is an explicit user
 * choice (`from`), never the default — a three-week stay would otherwise ship
 * every observation to draw one chart.
 */
export function fetchVitals(admissionId: number, from?: string, to?: string, includeSuperseded = false): Promise<Vitals[]> {
  return api.get(ip(admissionId, '/vitals'), {
    query: { from, to, include_superseded: includeSuperseded ? 1 : undefined },
  });
}

/** Never an edit: creates a new version and supersedes the original, which
 * stays on record. A reason is mandatory; send only the values that change. */
export function amendVitals(
  vitalsId: number, reason: string, changes: Partial<Vitals>
): Promise<Vitals> {
  return api.post(`/ip/vitals/${vitalsId}/amend`, { ...changes, amendment_reason: reason });
}

export function recordVitals(admissionId: number, input: Partial<Vitals>): Promise<Vitals> {
  return api.post(ip(admissionId, '/vitals'), input);
}

// ── Fluid balance ────────────────────────────────────────────────

export function fetchFluidBalance(admissionId: number, from?: string, to?: string): Promise<FluidBalance> {
  return api.get(ip(admissionId, '/fluid-balance'), { query: { from, to } });
}

export function recordFluid(admissionId: number, input: {
  direction: FluidDirection; category: string; quantity_ml: number;
  recorded_datetime?: string; shift?: string; route_detail?: string; remarks?: string;
}): Promise<unknown> {
  return api.post(ip(admissionId, '/fluid-balance'), input);
}

export function deleteFluidEntry(entryId: number): Promise<{ removed: true }> {
  return api.delete(`/ip/fluid-balance/${entryId}`);
}

// ── Medications ──────────────────────────────────────────────────

export function fetchMedicationOrders(admissionId: number, status?: string): Promise<MedicationOrder[]> {
  return api.get(ip(admissionId, '/medication-orders'), { query: { status } });
}

export function createMedicationOrder(admissionId: number, input: MedicationOrderInput): Promise<MedicationOrder> {
  return api.post(ip(admissionId, '/medication-orders'), input);
}

export function updateMedicationOrder(orderId: number, input: Partial<MedicationOrderInput> & { modification_reason?: string }, ifMatch?: string): Promise<MedicationOrder> {
  return api.patch(`/ip/medication-orders/${orderId}`, input, { ifMatch });
}

// Three explicit transitions rather than a patchable status, so each carries
// its own precondition and its own recorded reason.
export function holdOrder(orderId: number, reason: string): Promise<MedicationOrder> {
  return api.post(`/ip/medication-orders/${orderId}/hold`, { reason });
}
export function resumeOrder(orderId: number): Promise<MedicationOrder> {
  return api.post(`/ip/medication-orders/${orderId}/resume`);
}
export function discontinueOrder(orderId: number, reason: string): Promise<MedicationOrder> {
  return api.post(`/ip/medication-orders/${orderId}/discontinue`, { reason });
}

/** Sells through the same engine as every other pharmacy front door — see
 * routes/ip/medications.js's own header for why every line must match this
 * order's own drug. */
export function dispenseOrder(orderId: number, input: DispenseOrderInput): Promise<DispenseOrderResult> {
  return api.post(`/ip/medication-orders/${orderId}/dispense`, input);
}

/**
 * Recording that a dose was actually given (or deliberately not given).
 *
 * A separate call from anything that touches the order, because an
 * administration is a separate clinical event — never inferred from the order,
 * the prescription, the bill or the inventory.
 */
export function recordAdministration(orderId: number, input: AdministrationInput): Promise<MedicationAdministration> {
  return api.post(`/ip/medication-orders/${orderId}/administrations`, input);
}

export function updateAdministration(adminId: number, input: Partial<AdministrationInput>, ifMatch?: string): Promise<MedicationAdministration> {
  return api.patch(`/ip/medication-administrations/${adminId}`, input, { ifMatch });
}

/** Due slots (computed) joined against what was recorded. */
export function fetchMar(admissionId: number, date?: string): Promise<Mar> {
  return api.get(ip(admissionId, '/mar'), { query: { date } });
}

// ── Investigations / procedures / consults / care plan ───────────

export function fetchInvestigations(admissionId: number): Promise<Investigation[]> {
  return api.get(ip(admissionId, '/investigations'));
}
export function createInvestigation(admissionId: number, input: InvestigationInput): Promise<Investigation> {
  return api.post(ip(admissionId, '/investigations'), input);
}
export function updateInvestigation(id: number, input: Partial<InvestigationInput>, ifMatch?: string): Promise<Investigation> {
  return api.patch(`/ip/investigations/${id}`, input, { ifMatch });
}
/** The attestation that clears a critical-result alert. */
export function reviewInvestigation(id: number, result_summary?: string): Promise<Investigation> {
  return api.post(`/ip/investigations/${id}/review`, { result_summary });
}

export function fetchProcedures(admissionId: number): Promise<Procedure[]> {
  return api.get(ip(admissionId, '/procedures'));
}
export function createProcedure(admissionId: number, input: ProcedureInput): Promise<Procedure> {
  return api.post(ip(admissionId, '/procedures'), input);
}
export function updateProcedure(id: number, input: Partial<ProcedureInput>, ifMatch?: string): Promise<Procedure> {
  return api.patch(`/ip/procedures/${id}`, input, { ifMatch });
}

export function fetchConsults(admissionId: number): Promise<Consult[]> {
  return api.get(ip(admissionId, '/consults'));
}
export function createConsult(admissionId: number, input: ConsultInput): Promise<Consult> {
  return api.post(ip(admissionId, '/consults'), input);
}
export function setConsultStatus(
  id: number, status: ConsultStatus, extra: { response_notes?: string; recommendations?: string } = {}
): Promise<Consult> {
  return api.post(`/ip/consults/${id}/status`, { status, ...extra });
}

export function fetchCarePlan(admissionId: number): Promise<CarePlanActivity[]> {
  return api.get(ip(admissionId, '/care-plan'));
}
export function createCarePlanActivity(admissionId: number, input: CarePlanInput): Promise<CarePlanActivity> {
  return api.post(ip(admissionId, '/care-plan'), input);
}
export function updateCarePlanActivity(id: number, input: Partial<CarePlanInput> & { status?: string }, ifMatch?: string): Promise<CarePlanActivity> {
  return api.patch(`/ip/care-plan/${id}`, input, { ifMatch });
}
export function completeCarePlanActivity(id: number, notes?: string): Promise<CarePlanActivity> {
  return api.post(`/ip/care-plan/${id}/complete`, { notes });
}

// ── Transfers ────────────────────────────────────────────────────

export function fetchTransfers(admissionId: number): Promise<BedTransfer[]> {
  return api.get(ip(admissionId, '/transfers'));
}

/** Moves the bed. Does NOT close the admission — the episode is continuous. */
export function transferBed(admissionId: number, bed_id: number, reason?: string): Promise<BedTransfer> {
  return api.post(ip(admissionId, '/transfer'), { bed_id, reason });
}

// ── Face Sheet — admission particulars, attenders, MLC, payer ────
// Phase 1 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.1-6.4).

export function fetchParticulars(admissionId: number): Promise<AdmissionParticulars> {
  return api.get(ip(admissionId, '/particulars'));
}
export function updateParticulars(admissionId: number, input: ParticularsInput): Promise<AdmissionParticulars> {
  return api.patch(ip(admissionId, '/particulars'), input);
}

export function fetchAttenders(admissionId: number): Promise<Attender[]> {
  return api.get(ip(admissionId, '/attenders'));
}
export function createAttender(admissionId: number, input: AttenderInput): Promise<Attender> {
  return api.post(ip(admissionId, '/attenders'), input);
}
export function updateAttender(id: number, input: Partial<AttenderInput>): Promise<Attender> {
  return api.patch(`/ip/attenders/${id}`, input);
}
export function deleteAttender(id: number): Promise<void> {
  return api.delete(`/ip/attenders/${id}`);
}

/** Null when no MLC row has been recorded yet — "not yet answered", not an error. */
export function fetchMlc(admissionId: number): Promise<Mlc | null> {
  return api.get(ip(admissionId, '/mlc'));
}
/** Idempotent upsert — safe to call repeatedly as the form fills in. */
export function saveMlc(admissionId: number, input: MlcInput): Promise<Mlc> {
  return api.put(ip(admissionId, '/mlc'), input);
}

// Phase 9 (§9.4) — admission-level legal hold.
export function fetchLegalHold(admissionId: number): Promise<LegalHold> {
  return api.get(ip(admissionId, '/legal-hold'));
}
/** Manual (litigation) hold only — MLC and death holds engage automatically. */
export function setLegalHold(admissionId: number, reason: string): Promise<LegalHold> {
  return api.post(ip(admissionId, '/legal-hold'), { reason });
}
export function clearLegalHold(admissionId: number, reason: string): Promise<LegalHold> {
  return api.post(ip(admissionId, '/legal-hold/clear'), { reason });
}

export function fetchPayerSchemes(): Promise<PayerScheme[]> {
  return api.get('/ip/payer-schemes');
}
export function fetchPayers(admissionId: number): Promise<AdmissionPayer[]> {
  return api.get(ip(admissionId, '/payers'));
}
export function createPayer(admissionId: number, input: PayerInput): Promise<AdmissionPayer> {
  return api.post(ip(admissionId, '/payers'), input);
}
export function updatePayer(id: number, input: Partial<PayerInput>): Promise<AdmissionPayer> {
  return api.patch(`/ip/payers/${id}`, input);
}
export function deletePayer(id: number): Promise<void> {
  return api.delete(`/ip/payers/${id}`);
}
/** Stamps the "ID proof verified by" line — server-supplied actor/timestamp only. */
export function verifyPayerId(id: number): Promise<AdmissionPayer> {
  return api.post(`/ip/payers/${id}/verify-id`, {});
}
/** The insurer/TPA picker — Admin Panel's payer master (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.2). */
export function fetchPayersMaster(q?: string): Promise<PayerMasterOption[]> {
  return api.get(`/payers${q ? `?q=${encodeURIComponent(q)}` : ''}`);
}

// ── Consent register ──────────────────────────────────────────────
// Phase 2 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.5). Append-only
// — there is no update function; see createConsent/withdrawConsent.

export function fetchConsents(admissionId: number): Promise<Consent[]> {
  return api.get(ip(admissionId, '/consents'));
}
export function createConsent(admissionId: number, input: ConsentInput): Promise<Consent> {
  return api.post(ip(admissionId, '/consents'), input);
}
/** Inserts a new row referencing `id` via supersedes_id — the original is never edited. */
export function withdrawConsent(id: number, reason: string): Promise<Consent> {
  return api.post(`/ip/consents/${id}/withdraw`, { reason });
}

// ── Nursing assessment & risk screens ──────────────────────────────
// Phase 3 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.7).

export function fetchNursingAssessment(admissionId: number): Promise<NursingAssessment | null> {
  return api.get(ip(admissionId, '/nursing-assessment'));
}
export function saveNursingAssessment(admissionId: number, input: NursingAssessmentInput): Promise<NursingAssessment> {
  return api.put(ip(admissionId, '/nursing-assessment'), input);
}
export function signNursingAssessment(admissionId: number): Promise<NursingAssessment> {
  return api.post(ip(admissionId, '/nursing-assessment/sign'));
}

export function fetchRiskScreens(admissionId: number, screenType?: string): Promise<RiskScreen[]> {
  return api.get(ip(admissionId, '/risk-screens'), screenType ? { query: { screen_type: screenType } } : undefined);
}
/** score/band are always computed server-side from `items` — never sent. */
export function createRiskScreen(admissionId: number, input: RiskScreenInput): Promise<RiskScreen> {
  return api.post(ip(admissionId, '/risk-screens'), input);
}

// ── Clinical orders (Part B1) ────────────────────────────────────────
// Phase 4 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.8).

export function fetchClinicalOrders(admissionId: number): Promise<ClinicalOrder[]> {
  return api.get(ip(admissionId, '/clinical-orders'));
}
export function createClinicalOrder(admissionId: number, input: ClinicalOrderInput): Promise<ClinicalOrder> {
  return api.post(ip(admissionId, '/clinical-orders'), input);
}
export function countersignClinicalOrder(id: number): Promise<ClinicalOrder> {
  return api.post(`/ip/clinical-orders/${id}/countersign`, {});
}
export function discontinueClinicalOrder(id: number, reason: string): Promise<ClinicalOrder> {
  return api.post(`/ip/clinical-orders/${id}/discontinue`, { reason });
}
/** Only valid once an order is finalized (written on entry, or countersigned) — see guards.js. */
export function amendClinicalOrder(id: number, amendment_reason: string, changes: Partial<ClinicalOrderInput>): Promise<ClinicalOrder> {
  return api.post(`/ip/clinical-orders/${id}/amend`, { amendment_reason, ...changes });
}

// ── Admission diagnoses (hybrid free-text + optional ICD-10) ────────
// Phase 4 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.9). Append-only
// — there is no update function; see createDiagnosis/supersedeDiagnosis.

export function fetchDiagnoses(admissionId: number): Promise<AdmissionDiagnosis[]> {
  return api.get(ip(admissionId, '/diagnoses'));
}
export function createDiagnosis(admissionId: number, input: DiagnosisInput): Promise<AdmissionDiagnosis> {
  return api.post(ip(admissionId, '/diagnoses'), input);
}
/** Inserts a new row referencing `id` — the original is never edited. */
export function supersedeDiagnosis(id: number, changes: Partial<DiagnosisInput>): Promise<AdmissionDiagnosis> {
  return api.post(`/ip/diagnoses/${id}/supersede`, changes);
}

// ── Discharge summary (incl. death variant) ──────────────────────────
// Phase 5 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.11). Append-only
// once signed — see putDischargeSummary/signDischargeSummary/amendDischargeSummary.

export function fetchDischargeSummary(admissionId: number): Promise<DischargeSummary | null> {
  return api.get(ip(admissionId, '/discharge-summary'));
}
/** Creates the draft if none exists yet, otherwise updates it in place — refused once signed. */
export function putDischargeSummary(admissionId: number, input: DischargeSummaryInput): Promise<DischargeSummary> {
  return api.put(ip(admissionId, '/discharge-summary'), input);
}
export function signDischargeSummary(id: number): Promise<DischargeSummary> {
  return api.post(`/ip/discharge-summaries/${id}/sign`, {});
}
/** Inserts a new draft row referencing `id` — the signed original is never edited. */
export function amendDischargeSummary(id: number, amendment_reason: string, changes: DischargeSummaryInput): Promise<DischargeSummary> {
  return api.post(`/ip/discharge-summaries/${id}/amend`, { amendment_reason, ...changes });
}
export function fetchDischargeSummaryHistory(id: number): Promise<DischargeSummary[]> {
  return api.get(`/ip/discharge-summaries/${id}/history`);
}

// ── Alert rules ──────────────────────────────────────────────────

export function fetchAlertRules(): Promise<AlertRule[]> {
  return api.get('/ip/alert-rules');
}

export function updateAlertRule(
  ruleKey: string,
  patch: { enabled?: boolean; params?: Record<string, number>; severity?: string; clinical_basis?: string }
): Promise<AlertRule> {
  return api.patch(`/ip/alert-rules/${ruleKey}`, patch);
}

// ── Drug Master product search (routes/medicines.js) ──────────────
// Optional resolve-as-you-type for a medication order's drug field — see
// docs/HMS_v4_Drug_Master_Formulary_Architecture.md §23. Free-text ordering
// stays fully supported when nothing is picked; medicine_id is never
// required.
export function searchMedicines(q: string): Promise<MedicineSearchResult[]> {
  if (!q.trim()) return Promise.resolve([]);
  return api.get('/medicines', { query: { q } });
}
